import { DirectionalHint } from '@fluentui/react';
import { IPCallout } from 'baseComponents';
import InlineSuggestions from 'components/Suggestions/InlineSuggestions';
import KPI from 'models/kpi/kpi';
import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findSimilarByName } from 'utils/nameSimilarity';

interface IReuseSuggestionsProps {
  name: string;
  candidates: KPI[];
  onReuse: (kpi: KPI) => void;
  target: RefObject<HTMLElement>;
  isOpen?: boolean;
  /** Bump this to force the callout open again after the user dismissed it. */
  reopenSignal?: number;
}

// Advisory "you may be duplicating an existing field" hint. Shown as a callout so the form layout
// does not jump when matches appear while the user types a name. The body is the shared, always
// expanded InlineSuggestions panel (this component owns translation; InlineSuggestions does not).
const ReuseSuggestions = (props: IReuseSuggestionsProps) => {
  const { t } = useTranslation(['kpis']);
  const [dismissed, setDismissed] = useState(false);
  const wasPanelOpen = useRef(false);
  const matches = useMemo(
    () => findSimilarByName(props.name, props.candidates, (k) => k.name, 4),
    [props.name, props.candidates],
  );

  // Dismiss/reopen state machine: a dismissal only holds for the CURRENT suggestion set. Three
  // triggers clear it: (1) the host panel transitioning closed->open, (2) the parent bumping
  // reopenSignal ("show existing" link), and (3) fresh matches appearing after having none. The
  // third is adjusted during render (not in an effect) so the reopened callout paints in the same
  // commit as the new matches.
  useEffect(() => {
    const panelOpen = props.isOpen ?? true;
    if (panelOpen && !wasPanelOpen.current) {
      setDismissed(false);
    }
    wasPanelOpen.current = panelOpen;
  }, [props.isOpen]);

  useEffect(() => {
    if (props.reopenSignal) setDismissed(false);
  }, [props.reopenSignal]);

  const [hadMatches, setHadMatches] = useState(false);
  const hasMatches = matches.length > 0;
  if (hasMatches !== hadMatches) {
    setHadMatches(hasMatches);
    if (hasMatches) setDismissed(false);
  }

  const dismissCallout = () => setDismissed(true);
  const isOpen = matches.length > 0 && !dismissed;

  if (!isOpen) {
    return null;
  }

  return (
    <IPCallout
      target={props.target}
      directionalHint={DirectionalHint.bottomLeftEdge}
      isBeakVisible={true}
      onDismiss={dismissCallout}
      setInitialFocus={false}
      role="dialog"
      ariaLabel={t('kpis:Reuse.SimilarCalloutTitle')}
    >
      <InlineSuggestions
        items={matches.map((m) => ({ id: m.item.kpiId, label: m.item.name, kpi: m.item }))}
        onSelect={(item) => props.onReuse(item.kpi)}
        expanded={true}
        onExpandedChange={(next) => {
          if (!next) dismissCallout();
        }}
        text={{
          label: t('kpis:Reuse.SimilarCalloutTitle'),
          hint: t('kpis:Reuse.SimilarCalloutDescription'),
          dismiss: t('kpis:Reuse.SimilarCalloutDismiss'),
        }}
      />
    </IPCallout>
  );
};

export default ReuseSuggestions;
